/* Cart drawer matches the product-card rounding system, includes the tuinplanten minimum order rule, and shows delivery costs and payment methods.
   Updated: Checkout CTA now opens the dedicated cart overview page.
   Updated: Personal-service WhatsApp and phone links now use the real number +31 85 02 33 564.
   Updated: Added pickup discount banner for AFHALEN20 coupon.
   Updated: Added DataFast cart_opened goal when drawer opens with items. */
import { useCart } from '@/contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Minus, ShoppingBag, Mail, Phone, CircleAlert as AlertCircle, Truck, Trash2, ShoppingCart, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRouter } from '@/hooks/useNextRouter';
import { useState, useEffect } from 'react';
import { calculatePriceExclBTW } from '@/lib/btw-calculator';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { trackGoal } from '@/lib/datafast';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function CartDrawer({ open, onClose }: CartDrawerProps) {
  const {
    items,
    total,
    totalDiscount,
    hasMultipleSuppliers,
    exceedsPalletLimit,
    totalPalletPlaces,
    removeItem,
    updateQuantity,
  } = useCart();
  const router = useRouter();
  const [editingQuantity, setEditingQuantity] = useState<{ [key: string]: string }>({});
  const [deliverySettings, setDeliverySettings] = useState<{
    tuinplanten_fee_cents: number;
    haagplanten_fee_cents: number;
    bomen_fee_cents?: number;
    free_from_amount_cents: number;
    tuinplanten_minimum_cents?: number;
  } | null>(null);

  useEffect(() => {
    supabase
      .from('delivery_settings')
      .select('setting_value')
      .eq('setting_key', 'category_delivery_fees')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.setting_value) setDeliverySettings(data.setting_value);
      });
  }, []);

  const formatPrice = (price: number) => new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);

  const subtotalExclBTW = items.reduce((sum, item) => {
    const btwPercentage = item.btw_percentage || 9;
    return sum + calculatePriceExclBTW(item.unit_price, btwPercentage) * item.quantity;
  }, 0);
  const btwAmount = total - subtotalExclBTW;

  const hasTuinplanten = items.some(item => item.category_slug === 'tuinplanten');
  const tuinplantenTotal = items.reduce(
    (sum, item) => item.category_slug === 'tuinplanten' ? sum + item.unit_price * item.quantity : sum,
    0,
  );
  const tuinplantenMinimum = (deliverySettings?.tuinplanten_minimum_cents || 5000) / 100;
  const meetsTuinplantenMinimum = !hasTuinplanten || tuinplantenTotal >= tuinplantenMinimum;

  const deliveryDetails = deliverySettings
    ? (() => {
        const freeFrom = deliverySettings.free_from_amount_cents / 100;
        const hasHaagplanten = items.some(item => item.category_slug === 'haagplanten');
        const hasBomen = items.some(item => item.category_slug === 'bomen');
        const fee = hasTuinplanten ? deliverySettings.tuinplanten_fee_cents / 100 : 0;
        const categoryFee = fee
          + (hasHaagplanten ? deliverySettings.haagplanten_fee_cents / 100 : 0)
          + (hasBomen ? (deliverySettings.bomen_fee_cents || deliverySettings.haagplanten_fee_cents) / 100 : 0);
        const isFree = total >= freeFrom;
        return {
          freeFrom,
          fee: isFree ? 0 : categoryFee,
          isFree,
          progress: Math.min(100, (total / freeFrom) * 100),
        };
      })()
    : null;
  const grandTotal = total + (deliveryDetails?.fee || 0);
  const isCheckoutBlocked = hasMultipleSuppliers || exceedsPalletLimit || !meetsTuinplantenMinimum;

  const handleCheckout = () => {
    if (isCheckoutBlocked) return;
    onClose();
    router.push('/winkelwagen');
  };

  useEffect(() => {
    if (open && items.length > 0) trackGoal('cart_opened');
  }, [open, items.length]);

  const generateWhatsAppMessage = () => {
    const productList = items.map(item => `${item.product_name} (${item.variant_size}) - ${item.quantity}x`).join(', ');
    const message = `Ik moest contact opnemen voor persoonlijke service vanwege de selectie van planten in mijn winkelwagen: ${productList}`;
    return `https://wa.me/31850233564?text=${encodeURIComponent(message)}`;
  };

  const emailLink = 'mailto:info@buitnplanten.nl?subject=Persoonlijke service voor bestelling&body=Ik heb producten van meerdere leveranciers in mijn winkelwagen en zou graag persoonlijke service willen ontvangen.';
  const phoneLink = 'tel:+31850233564';

  const handleQuantityChange = (itemId: string, value: string) => {
    setEditingQuantity({ ...editingQuantity, [itemId]: value });
  };

  const handleQuantityBlur = (itemId: string, minOrderQty?: number) => {
    const value = editingQuantity[itemId];
    if (value === undefined) return;

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      if (minOrderQty && numValue < minOrderQty) {
        toast.error(`Minimum bestelhoeveelheid is ${minOrderQty} stuks`);
      } else {
        updateQuantity(itemId, numValue);
      }
    }

    const newState = { ...editingQuantity };
    delete newState[itemId];
    setEditingQuantity(newState);
  };

  const handleQuantityKeyDown = (
    event: React.KeyboardEvent,
    itemId: string,
    minOrderQty?: number,
  ) => {
    if (event.key === 'Enter') {
      handleQuantityBlur(itemId, minOrderQty);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        className="flex w-full flex-col gap-0 overflow-hidden border-l border-[#e7e1d6] bg-[#fcfaf6] p-0 text-[#1a1a1a] shadow-2xl sm:max-w-[460px]"
      >
        <SheetHeader className="shrink-0 border-b border-[#e7e1d6] bg-white px-6 pb-5 pt-7 pr-16 text-left">
          <SheetTitle className="font-playfair text-3xl font-semibold tracking-tight text-[#1e2b22]">
            Jouw winkelwagen
          </SheetTitle>
          <div className="mt-4 text-sm text-[#5a6552]">
            {items.length === 0 ? 'Je winkelwagen is leeg' : `${items.reduce((sum, item) => sum + item.quantity, 0)} ${items.reduce((sum, item) => sum + item.quantity, 0) === 1 ? 'product' : 'producten'}`}
          </div>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-[12px] border border-[#5a6552] bg-white text-[#5a6552]">
              <ShoppingBag className="h-8 w-8" strokeWidth={1.4} />
            </div>
            <h2 className="font-playfair text-2xl font-semibold text-[#1e2b22]">Je winkelwagen is leeg</h2>
            <p className="mt-2 max-w-xs text-sm leading-6 text-[#5a6552]">Voeg mooie planten toe om jouw tuin compleet te maken.</p>
            <Button asChild onClick={onClose} className="mt-7 h-12 rounded-md bg-[#1e2b22] px-7 text-sm font-semibold text-white hover:bg-[#5a6552]">
              <Link to="/haagplanten">Bekijk assortiment</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="mb-5 rounded-[12px] border border-[#1e2b22]/15 bg-[#1e2b22]/5 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-white text-[#1e2b22]">
                      <Tag className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#1e2b22]">
                        20% korting bij afhalen
                      </p>
                      <p className="mt-1 text-xs leading-5 text-[#5a6552]">
                        Gebruik code <span className="font-semibold text-[#1e2b22]">AFHALEN20</span> bij afhalen in Dedemsvaart. Geldig voor producten van eigen kwekerij.
                      </p>
                    </div>
                  </div>
                </div>

              {deliveryDetails && !deliveryDetails.isFree && (!hasTuinplanten || meetsTuinplantenMinimum) && (
                <div className="mb-5 rounded-[12px] border border-[#e7e1d6] bg-[#f3efe7] p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-white text-[#5a6552]">
                      <Truck className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#1e2b22]">
                        {deliveryDetails.isFree ? 'Gratis bezorging bereikt' : `Nog ${formatPrice(deliveryDetails.freeFrom - total)} voor gratis bezorging`}
                      </p>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white">
                        <div className="h-full rounded-full bg-[#5a6552] transition-all duration-500" style={{ width: `${deliveryDetails.progress}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="group relative border-b border-[#e7e1d6] pb-4 last:border-b-0">
                    <div className="flex gap-3">
                      <div className="h-[104px] w-[88px] shrink-0 overflow-hidden rounded-tr-[12px] rounded-bl-[12px] bg-white ring-1 ring-[#e7e1d6]">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-[#cfcac2]"><ShoppingBag className="h-7 w-7" /></div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <Link to={item.slug ? `/${item.slug}` : '#'} onClick={onClose} className="min-w-0 pr-1">
                            <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-[#1e2b22] transition-colors hover:text-[#5a6552] hover:underline">
                              {item.product_name}{item.variant_size ? ` ${item.variant_size}` : ''}
                            </h3>
                          </Link>
                          <button type="button" onClick={() => removeItem(item.id)} aria-label={`Verwijder ${item.product_name}`} className="shrink-0 text-[#9b958c] transition-colors hover:text-[#1e2b22]">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {item.variant_type && (
                          <div className="mt-2 inline-flex items-center gap-1.5 rounded-[5px] border border-[#5a6552] bg-white px-2 py-1 text-[11px] font-medium text-[#5a6552]">
                            <img
                              src={`/assets/${item.variant_type === 'Kale wortel' ? 'kale-wortel.webp' : item.variant_type === 'Kluit' ? 'met-kluit.webp' : 'in-pot.webp'}`}
                              alt=""
                              className="h-4 w-4 object-contain"
                            />
                            {item.variant_type}
                          </div>
                        )}

                        <div className="mt-3 flex items-end justify-between gap-2">
                          <div className="flex h-8 items-center rounded-full border border-[#5a6552] bg-white">
                            <Button variant="ghost" size="icon" aria-label="Aantal verminderen" className="h-7 w-7 rounded-full text-[#5a6552] hover:bg-[#f3efe7] hover:text-[#1e2b22]" onClick={() => {
                              const newQuantity = item.quantity - 1;
                              if (item.minimum_order_quantity && newQuantity < item.minimum_order_quantity && newQuantity > 0) {
                                toast.error(`Minimum bestelhoeveelheid is ${item.minimum_order_quantity} stuks`);
                                return;
                              }
                              updateQuantity(item.id, newQuantity);
                            }}>
                              <Minus className="h-3.5 w-3.5" />
                            </Button>
                            <Input
                              type="text"
                              aria-label={`Aantal ${item.product_name}`}
                              value={editingQuantity[item.id] ?? item.quantity}
                              onChange={(event) => handleQuantityChange(item.id, event.target.value)}
                              onBlur={() => handleQuantityBlur(item.id, item.minimum_order_quantity)}
                              onKeyDown={(event) => handleQuantityKeyDown(event, item.id, item.minimum_order_quantity)}
                              className="h-7 w-9 border-0 bg-transparent p-0 text-center text-xs font-semibold text-[#1e2b22] focus-visible:ring-0"
                            />
                            <Button variant="ghost" size="icon" aria-label="Aantal verhogen" className="h-7 w-7 rounded-full text-[#5a6552] hover:bg-[#f3efe7] hover:text-[#1e2b22]" onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-[#1e2b22]">{formatPrice(item.unit_price * item.quantity)}</p>
                            <p className="text-[11px] text-[#8b867d]">{formatPrice(item.unit_price)} per stuk</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasTuinplanten && !meetsTuinplantenMinimum && (
                <div className="mb-5 rounded-[12px] border border-[#e7e1d6] bg-[#f3efe7] p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-white text-[#5a6552]">
                      <ShoppingCart className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#1e2b22]">
                        Nog {formatPrice(tuinplantenMinimum - tuinplantenTotal)} voor minimum bestelbedrag
                      </p>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white">
                        <div className="h-full rounded-full bg-[#5a6552] transition-all duration-500" style={{ width: `${Math.min(100, (tuinplantenTotal / tuinplantenMinimum) * 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {hasMultipleSuppliers && (
                <Alert className="mt-5 rounded-[12px] border-[#d8c69c] bg-[#fff8e9] text-[#6e5520]">
                  <AlertCircle className="h-4 w-4 text-[#a47b21]" />
                  <AlertDescription className="text-xs leading-5">
                    <p className="font-semibold">Persoonlijke service nodig</p>
                    <p className="mt-1">Voor deze combinatie van planten helpen we je graag persoonlijk verder.</p>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Button size="sm" variant="outline" className="h-9 border-[#d8c69c] bg-white px-2 text-[11px] text-[#6e5520] hover:bg-[#f3e8c9]" onClick={() => window.open(generateWhatsAppMessage(), '_blank')}>
                        WhatsApp
                      </Button>
                      <Button size="sm" variant="outline" className="h-9 border-[#d8c69c] bg-white px-2 text-[11px] text-[#6e5520] hover:bg-[#f3e8c9]" onClick={() => { window.location.href = emailLink; }}>
                        <Mail className="mr-1 h-3.5 w-3.5" /> Mail
                      </Button>
                      <Button size="sm" variant="outline" className="h-9 border-[#d8c69c] bg-white px-2 text-[11px] text-[#6e5520] hover:bg-[#f3e8c9]" onClick={() => { window.location.href = phoneLink; }}>
                        <Phone className="mr-1 h-3.5 w-3.5" /> Bel
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {exceedsPalletLimit && (
                <Alert className="mt-4 rounded-[12px] border-[#d8c69c] bg-[#fff8e9] text-[#6e5520]">
                  <AlertCircle className="h-4 w-4 text-[#a47b21]" />
                  <AlertDescription className="text-xs leading-5">Uw bestelling overschrijdt de maximale capaciteit ({totalPalletPlaces} / 15 palletplaatsen). Neem contact met ons op voor een offerte.</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="shrink-0 border-t border-[#e7e1d6] bg-white px-5 pb-5 pt-4 sm:px-6">
              <div className="space-y-2 text-sm text-[#5a6552]">
                <div className="flex justify-between"><span>Subtotaal excl. BTW</span><span className="font-medium text-[#1e2b22]">{formatPrice(subtotalExclBTW)}</span></div>
                <div className="flex justify-between"><span>BTW</span><span className="font-medium text-[#1e2b22]">{formatPrice(btwAmount)}</span></div>
                {deliveryDetails && <div className="flex justify-between"><span>Bezorgkosten</span><span className={`font-medium ${deliveryDetails.isFree ? 'text-[#5a6552]' : 'text-[#1e2b22]'}`}>{deliveryDetails.isFree ? 'Gratis' : formatPrice(deliveryDetails.fee)}</span></div>}
                {totalDiscount > 0 && <div className="flex justify-between text-[#5a6552]"><span>Jouw voordeel</span><span className="font-medium">-{formatPrice(totalDiscount)}</span></div>}
              </div>
              <div className="mt-4 flex items-end justify-between border-t border-[#e7e1d6] pt-4">
                <div><p className="text-xs uppercase tracking-[0.16em] text-[#8b867d]">Totaal incl. BTW</p><p className="mt-1 text-2xl font-semibold text-[#1e2b22]">{formatPrice(grandTotal)}</p></div>
              </div>
              <Button onClick={handleCheckout} disabled={isCheckoutBlocked} className="mt-4 h-13 w-full rounded-none rounded-tl-[12px] rounded-br-[12px] bg-[#1e2b22] py-6 text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#5a6552] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50">
                {hasMultipleSuppliers || exceedsPalletLimit ? 'Neem contact op' : !meetsTuinplantenMinimum ? 'Minimum bestelbedrag niet bereikt' : 'Afrekenen'}
              </Button>
              <Button asChild onClick={onClose} variant="outline" className="mt-2 h-12 w-full rounded-none rounded-tl-[12px] rounded-br-[12px] border-[#5a6552] bg-white text-sm font-semibold text-[#1e2b22] hover:bg-[#f3efe7] hover:text-[#1e2b22]">
                <Link to="/haagplanten">Verder winkelen</Link>
              </Button>
              <div className="mt-4 border-t border-[#e7e1d6] pt-4">
                <p className="text-center text-[11px] font-medium uppercase tracking-[0.16em] text-[#8b867d]">Betaal eenvoudig met</p>
                <div className="mt-3 flex items-center justify-center gap-3">
                  <img src="/assets/payments/ideal-wero.svg" alt="iDEAL en Wero" className="h-6 w-auto" />
                  <img src="/assets/payments/apple-pay.svg" alt="Apple Pay" className="h-6 w-auto" />
                  <img src="/assets/payments/bancontact.svg" alt="Bancontact" className="h-6 w-auto" />
                  <img src="/assets/payments/sepa.svg" alt="SEPA" className="h-6 w-auto" />
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
